/**
 * Queries over the province graph.
 *
 * All of these are pure reads of `CampaignState.links` — the graph itself is
 * state and lives there.
 *
 * They used to cache nothing, deliberately, and the reasoning was sound at the
 * time: twenty-four nodes and thirty-eight edges, a breadth-first search costing
 * less than deciding whether a cache is stale, and the path-cache bug in
 * `BALANCE.md` — twice — as the argument against being clever. The board is now
 * a hundred and twenty-seven nodes and a hundred and ninety-two edges, and
 * `neighbours` walks every edge on every call while `hopsFrom` calls
 * `neighbours` once per node. That is twenty-four thousand edge tests per
 * breadth-first search, and the AI council runs several per host per turn.
 *
 * So there is an index now, and it is built to be *unfalsifiable* rather than
 * merely careful. It is keyed on the `links` array itself — the exact object the
 * answer is a function of — in a `WeakMap`, and it is thrown away the instant
 * that array is replaced or changes length. Nothing in the campaign rewires an
 * existing link in place; a broken road replaces the array. If that ever stops
 * being true the index has to go, and `tests/campaign.test.ts` asserts the
 * property rather than trusting this paragraph.
 *
 * The rule from `COORDINATION.md` still holds and is what makes this legal: a
 * derived cache must not be observable. Every function here returns exactly what
 * it returned before, including the arrays it hands out, which are copies.
 */

import type { CampaignState, ProvinceLink } from './types.ts';

interface GraphIndex {
  /** The array this index was built from, and how long it was. */
  links: ProvinceLink[];
  length: number;
  adjacency: number[][];
  /** Memoised breadth-first distances, by source. */
  hops: Map<number, number[]>;
  /** Link lookup by `a * stride + b`, a < b. */
  byPair: Map<number, ProvinceLink>;
  /** Which walkable island each province is on. Built on first ask. */
  components: number[] | null;
}

const INDEX = new WeakMap<ProvinceLink[], GraphIndex>();

function indexOf(c: CampaignState): GraphIndex {
  const hit = INDEX.get(c.links);
  if (hit && hit.length === c.links.length) return hit;

  const adjacency: number[][] = [];
  for (let i = 0; i < c.provinces.length; i++) adjacency.push([]);
  const byPair = new Map<number, ProvinceLink>();
  for (const l of c.links) {
    if (adjacency[l.a]) adjacency[l.a].push(l.b);
    if (adjacency[l.b]) adjacency[l.b].push(l.a);
    byPair.set(Math.min(l.a, l.b) * 8192 + Math.max(l.a, l.b), l);
  }
  for (const list of adjacency) list.sort((x, y) => x - y);

  const built: GraphIndex = {
    links: c.links,
    length: c.links.length,
    adjacency,
    hops: new Map(),
    byPair,
    components: null,
  };
  INDEX.set(c.links, built);
  return built;
}

/**
 * The neighbours of `p`, without the copy.
 *
 * Internal, because the array it returns is the index's own and a caller that
 * sorted or spliced it would corrupt the graph for everybody. Every exported
 * path copies.
 */
function adjacent(c: CampaignState, p: number): number[] {
  return indexOf(c).adjacency[p] ?? EMPTY;
}

const EMPTY: number[] = [];

/** Every province index directly linked to `p`. Ascending, so it is stable. */
export function neighbours(c: CampaignState, p: number): number[] {
  return adjacent(c, p).slice();
}

export function linkBetween(c: CampaignState, a: number, b: number): ProvinceLink | undefined {
  return indexOf(c).byPair.get(Math.min(a, b) * 8192 + Math.max(a, b));
}

export function areAdjacent(c: CampaignState, a: number, b: number): boolean {
  return linkBetween(c, a, b) !== undefined;
}

/**
 * Hops from `from` to every province, breadth-first. Unreachable provinces come
 * back as -1. Used by the AI to decide what counts as "near".
 */
export function hopsFrom(c: CampaignState, from: number): number[] {
  const idx = indexOf(c);
  const cached = idx.hops.get(from);
  // A copy, always. The AI reads these and one caller that decided to sort or
  // zero an entry would silently rewrite the map for every caller after it.
  if (cached) return cached.slice();

  const dist = new Array<number>(c.provinces.length).fill(-1);
  if (from < 0 || from >= dist.length) return dist;
  dist[from] = 0;
  const queue = [from];
  for (let head = 0; head < queue.length; head++) {
    const at = queue[head];
    for (const n of adjacent(c, at)) {
      if (dist[n] !== -1) continue;
      dist[n] = dist[at] + 1;
      queue.push(n);
    }
  }
  idx.hops.set(from, dist);
  return dist.slice();
}

/**
 * The next province to step to along a shortest path from `from` to `to`, or -1
 * if there is no route. Ties break toward the lowest index, which is arbitrary
 * but — far more importantly — is the same every time.
 */
export function stepToward(c: CampaignState, from: number, to: number): number {
  if (from === to) return from;
  const dist = hopsFrom(c, to);
  if (dist[from] < 0) return -1;
  let best = -1;
  let bestD = dist[from];
  for (const n of adjacent(c, from)) {
    if (dist[n] >= 0 && dist[n] < bestD) {
      bestD = dist[n];
      best = n;
    }
  }
  return best;
}

/** Provinces this nation holds. */
export function provincesOf(c: CampaignState, nation: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < c.provinces.length; i++) if (c.provinces[i].owner === nation) out.push(i);
  return out;
}

/** Held provinces that touch ground somebody else holds — the places that matter. */
export function frontierOf(c: CampaignState, nation: number): number[] {
  const out: number[] = [];
  for (const p of provincesOf(c, nation)) {
    if (adjacent(c, p).some((n) => c.provinces[n].owner !== nation)) out.push(p);
  }
  return out;
}

/** Everything this nation could march on from where it currently stands. */
export function targetsOf(c: CampaignState, nation: number): number[] {
  const seen = new Set<number>();
  for (const p of provincesOf(c, nation)) {
    for (const n of adjacent(c, p)) {
      if (c.provinces[n].owner !== nation) seen.add(n);
    }
  }
  return [...seen].sort((a, b) => a - b);
}


// ── Walkable islands ───────────────────────────────────────────────────────

/**
 * Which walkable island each province is on.
 *
 * This used to be a question nobody needed to ask, because the realm was one
 * component and the answer was always 0. It is now the most important fact
 * about a province: the road graph is in **seven** pieces and 111 of 150
 * provinces cannot be reached from the heartland on foot at all. "Can my hosts
 * walk from here to there" is a comparison of two of these numbers.
 *
 * Component ids are assigned by ascending lowest member, so island 0 always
 * contains province 0 and the numbering is the same on every run of the same
 * board — it is safe to key a colour or a legend entry off. It is *not* safe to
 * save: it is derived from `links`, which roads change.
 *
 * Built on first ask and cached in the same index `hopsFrom` uses, so it dies
 * on the same rule — the instant `links` is replaced or changes length. Roads
 * built during a campaign therefore re-merge the islands without anybody
 * invalidating anything by hand.
 */
function componentsOf(c: CampaignState): number[] {
  const idx = indexOf(c);
  if (idx.components && idx.components.length === c.provinces.length) return idx.components;

  const comp = new Array<number>(c.provinces.length).fill(-1);
  let next = 0;
  for (let i = 0; i < comp.length; i++) {
    if (comp[i] >= 0) continue;
    comp[i] = next;
    const stack = [i];
    while (stack.length > 0) {
      const p = stack.pop()!;
      for (const n of adjacent(c, p)) {
        if (comp[n] < 0) {
          comp[n] = next;
          stack.push(n);
        }
      }
    }
    next++;
  }
  idx.components = comp;
  return comp;
}

/** The island `p` stands on, or -1 if it is not a province. */
export function roadComponent(c: CampaignState, p: number): number {
  if (p < 0 || p >= c.provinces.length) return -1;
  return componentsOf(c)[p];
}

/**
 * Every province's island, in one array. A copy, for the same reason `hopsFrom`
 * copies: one caller that sorted it would rewrite the map for everybody after.
 */
export function roadComponents(c: CampaignState): number[] {
  return componentsOf(c).slice();
}

/** How many separate walkable islands the realm is in. Seven, on this board. */
export function roadComponentCount(c: CampaignState): number {
  const comp = componentsOf(c);
  let most = -1;
  for (const x of comp) if (x > most) most = x;
  return most + 1;
}

/**
 * Whether a host could walk from `a` to `b` at all, given roads alone and
 * ignoring who holds the ground in between.
 *
 * Deliberately says nothing about war, sieges or whose land it is — those are
 * `canMarch`'s business, one step at a time. This answers only the question the
 * sea made worth asking: is there a land route, or does this need hulls?
 */
export function canWalkBetween(c: CampaignState, a: number, b: number): boolean {
  const ca = roadComponent(c, a);
  return ca >= 0 && ca === roadComponent(c, b);
}
